import { useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';
import { useCheckoutElements } from '@stripe/react-stripe-js/checkout';
import { useMutation } from '@tanstack/react-query';

import { PaymentFailed } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import type {
  CheckoutSession,
  CheckoutSessionRequest,
  Price,
  TrialOffer,
} from 'src/api/endpoints/cloudConnect.types';
import { usePrice, useSetCountry, useTrialOffer } from 'src/api/queries';
import { useCheckoutSession } from 'src/api/queries/cloudConnectCheckout';
import { CHECKOUT_RETURN_PATH } from 'src/routes/_auth/_only-header/store/checkout-return';
import { COUNTRIES } from 'src/utils/countryList';

import { useCheckoutForm } from '../checkoutFormStore';
import type {
  CheckoutFormState,
  CheckoutFormStep,
} from '../components/Checkout.types';
import { toCheckoutAddress } from './checkoutAddress';
import {
  useCheckoutPromotion,
  type CheckoutPromotion,
} from './useCheckoutPromotion';

// Drives the address step, checkout-session creation, and payment
// confirmation (Stripe's Custom Checkout Elements). The confirmation
// screen itself is wired up in a follow-up PR.
type CheckoutFormSetter = ReturnType<typeof useCheckoutForm>[1];

type UseCheckoutBase = {
  checkoutFormState: CheckoutFormState;
  setCheckoutFormState: CheckoutFormSetter;
  startCheckoutSession: (nextState: CheckoutFormState) => Promise<void>;
  paymentStep: boolean;
  checkoutSession: ReturnType<typeof useCheckoutSession>;
  isLoadingSession: boolean;
  isLoadingPrice: boolean;
  isLoadingOffer: boolean;
  promotion: CheckoutPromotion;
};

type UseCheckoutLoading = UseCheckoutBase & {
  isLoading: true;
  error: Error | undefined;
  price: Price | undefined;
  trialOffer: TrialOffer | null | undefined;
};

type UseCheckoutError = UseCheckoutBase & {
  isLoading: false;
  error: Error;
  price: Price | undefined;
  trialOffer: TrialOffer | null | undefined;
};

type UseCheckoutReady = UseCheckoutBase & {
  isLoading: false;
  error: undefined;
  price: Price;
  trialOffer: TrialOffer | null;
};

export type UseCheckoutResult =
  | UseCheckoutLoading
  | UseCheckoutError
  | UseCheckoutReady;

const toCheckoutError = (error: unknown): Error | undefined => {
  if (!error) {
    return undefined;
  }
  return error instanceof Error ? error : new Error('Unknown checkout error.');
};

const toRequestBody = (
  form: CheckoutFormState,
  promotionCode?: string
): CheckoutSessionRequest => {
  return {
    name: `${form.firstName} ${form.lastName}`,
    address: toCheckoutAddress(form),
    ...(promotionCode ? { promotionCode } : {}),
  };
};

// Returns the checkout form state normalized for resuming a V2 checkout session.
const normalizeCheckoutV2ResumeState = (
  state: CheckoutFormState
): CheckoutFormState => ({
  ...state,
  currentStep: 'DETAILS',
  hasAcceptedTerms: false,
});

// Returns true if the checkout form state needs to be normalized for resuming
// a V2 checkout session. This is true if the user has progressed past the
// details step or has accepted terms.
const shouldNormalizeCheckoutV2ResumeState = (state: CheckoutFormState) =>
  state.currentStep !== 'DETAILS' || state.hasAcceptedTerms !== false;

const checkoutSessionSecretDelimiter = '_secret_';

const getCheckoutSessionId = (clientSecret: string) => {
  const delimiterIndex = clientSecret.indexOf(checkoutSessionSecretDelimiter);
  if (delimiterIndex <= 0) {
    throw new Error('Checkout session client secret is invalid.');
  }
  return clientSecret.slice(0, delimiterIndex);
};

// usePayment confirms the Stripe Checkout Session created by
// useCheckout/startCheckoutSession. Redirect-based payment methods (e.g.
// PayPal) navigate away and back via /store/checkout-return, so we always
// pass `redirect: 'always'` to keep a single code path for every payment
// method instead of branching on whether a redirect happened.
export const usePayment = (checkoutSession: CheckoutSession) => {
  const checkout = useCheckoutElements();
  const [checkoutFormState] = useCheckoutForm();

  return useMutation({
    mutationFn: async () => {
      if (checkout.type !== 'success') {
        throw new Error('Checkout is not ready yet');
      }

      const returnUrl = new URL(CHECKOUT_RETURN_PATH, window.location.origin);
      // The checkout session id is embedded in the client secret and is
      // needed to look up the payment outcome on return.
      const sessionId = getCheckoutSessionId(checkoutSession.clientSecret);
      if (!checkoutFormState.country) {
        throw new Error('Country is required to confirm checkout.');
      }
      returnUrl.searchParams.set('session_id', sessionId);
      returnUrl.searchParams.set('country', checkoutFormState.country);

      const result = await checkout.checkout.confirm({
        returnUrl: returnUrl.toString(),
        redirect: 'always',
      });
      // With `redirect: 'always'` the browser normally navigates away
      // before this promise resolves. It only resolves in-page when the
      // payment itself failed (e.g. card declined), so any 'error' result
      // here must be treated as a checkout failure rather than a silent
      // success.
      if (result.type === 'error') {
        throw new Error(result.error.message);
      }
    },
    onError(error) {
      Sentry.captureException(error);
      tracker.trackEvent(new PaymentFailed({ error_message: error.message }));
    },
  });
};

export const useCheckout = (): UseCheckoutResult => {
  const [checkoutFormState, setCheckoutFormState] = useCheckoutForm();
  const setCountry = useSetCountry();
  const checkoutSession = useCheckoutSession();
  const promotion = useCheckoutPromotion();
  const priceQuery = usePrice();
  const trialOfferQuery = useTrialOffer();
  const previousStepRef = useRef<CheckoutFormStep>(
    checkoutFormState.currentStep as CheckoutFormStep
  );

  // paymentStep is `true` when the user has added address information and
  // clicked the "next" button (ready to enter payment details)
  const paymentStep = Boolean(
    checkoutFormState.currentStep === 'PAYMENT' &&
    checkoutFormState.country &&
    checkoutFormState.firstName &&
    checkoutFormState.lastName
  );

  const startCheckoutSession = async (nextState: CheckoutFormState) => {
    if (!promotion.canContinue) {
      throw new Error('Promotion code must be validated before checkout.');
    }

    if (!nextState.country || !nextState.firstName || !nextState.lastName) {
      throw new Error('Missing required checkout details.');
    }

    setCheckoutFormState(nextState);
    await checkoutSession.mutateAsync(
      toRequestBody(nextState, promotion.appliedPromotion?.code)
    );
  };

  // clear cached checkout session when returning to step 1
  useEffect(() => {
    const previousStep = previousStepRef.current;
    const currentStep = checkoutFormState.currentStep as CheckoutFormStep;

    if (previousStep === 'PAYMENT' && currentStep === 'DETAILS') {
      checkoutSession.reset();
    }

    previousStepRef.current = currentStep;
  }, [checkoutFormState.currentStep, checkoutSession]);

  // The old and new checkout flows intentionally share persisted form fields,
  // so users can keep address/name prefill when moving between variants.
  // Workflow state is not reusable in V2: the Stripe session only lives in
  // memory and terms must be accepted for the current session. Always resume
  // on the details step and re-sync the country query cache to the persisted
  // address's country.
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (hasResumedRef.current) {
      return;
    }
    hasResumedRef.current = true;

    const resumeState = normalizeCheckoutV2ResumeState(checkoutFormState);
    if (shouldNormalizeCheckoutV2ResumeState(checkoutFormState)) {
      setCheckoutFormState(resumeState);
    }

    const persistedCountry = COUNTRIES.find(
      (c) => c.value === resumeState.country
    );
    if (persistedCountry) {
      void setCountry(persistedCountry);
    }
  }, [checkoutFormState, setCheckoutFormState, setCountry]);

  const price = priceQuery.data?.monthly;
  const trialOffer = trialOfferQuery.data;
  const isLoadingSession = checkoutSession.isPending;
  const isLoadingPrice =
    priceQuery.isPending || priceQuery.isFetching || checkoutSession.isPending;
  const isLoadingOffer =
    trialOfferQuery.isPending || trialOfferQuery.isFetching;
  const isLoading = isLoadingOffer || isLoadingPrice;
  const error =
    toCheckoutError(
      checkoutSession.error ?? trialOfferQuery.error ?? priceQuery.error
    ) ??
    (!isLoading && (price == null || trialOffer === undefined)
      ? new Error('Checkout data is unavailable.')
      : undefined);

  const resultBase: UseCheckoutBase = {
    checkoutFormState,
    setCheckoutFormState,
    startCheckoutSession,
    paymentStep,
    checkoutSession,
    isLoadingSession,
    isLoadingPrice,
    isLoadingOffer,
    promotion,
  };

  if (
    !isLoading &&
    error === undefined &&
    price !== undefined &&
    trialOffer !== undefined
  ) {
    return {
      ...resultBase,
      isLoading: false,
      error: undefined,
      price,
      trialOffer,
    };
  }

  if (isLoading) {
    return {
      ...resultBase,
      isLoading: true,
      error,
      price,
      trialOffer,
    };
  }

  return {
    ...resultBase,
    isLoading: false,
    error: error ?? new Error('Checkout failed.'),
    price,
    trialOffer,
  };
};
